import { ConfirmDialog } from "@/components/common/ConfirmDialog/ConfirmDialog"
import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { SETTINGS_PATHS } from "@/features/settings/paths"
import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { IconTrash } from "@tabler/icons-react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Settings, Trash2, UploadCloud } from "lucide-react"
import { type ReactNode, useState } from "react"
import { useNavigate } from "react-router"
import { apiClientV2 } from "../../../../services/api/apiClient"
import { toastApiError } from "../../api/apiError"
import { ModuleHeader } from "../shared/ModuleHeader"

interface MasterDataEntityPageProps {
  titleKey: TranslationKeys
  subtitleKey?: TranslationKeys
  children: ReactNode
  /** Tab-specific header button(s) (e.g. Substances' "Add substance"),
   *  rendered before the generic Reset/Import buttons so tab-specific
   *  actions read left-to-right first. Every OTHER caller (suppliers,
   *  articles, products, customers) omits this — purely additive. */
  headerActions?: ReactNode
  /** The article that explains this tab; the import, unless it has its own. */
  helpArticleId?: string
}

export function MasterDataEntityPage({
  titleKey,
  subtitleKey,
  children,
  headerActions,
  helpArticleId = "master-data.import",
}: MasterDataEntityPageProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const showReset = useFeatureFlag("MC_MASTER_DATA_RESET")
  const [showConfirm, setShowConfirm] = useState(false)
  // Field labels live in the shared template settings; the gear lands on that
  // section so the user never has to find it under the inquiry page.
  const queryClient = useQueryClient()

  const resetMutation = useMutation({
    mutationFn: () =>
      apiClientV2.delete("/material-compliance/master-data/reset"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["mc"] })
      setShowConfirm(false)
    },
    onError: (err) => {
      toastApiError(err, translate, "MC_MD_RESET_ERROR")
    },
  })

  return (
    <div className="flex h-full flex-col gap-4">
      <ModuleHeader
        title={translate(titleKey)}
        subtitle={subtitleKey ? translate(subtitleKey) : undefined}
        helpArticle={{
          categoryId: "master-data",
          articleId: helpArticleId,
        }}
        right={
          <div className="flex items-center gap-2">
            {headerActions}
            {/* The settings page saves through useUpdateTemplateSettings,
                which is guarded by MC_INQUIRIES_SEND, so the gear carries the
                same permission rather than opening a form nobody can submit. */}
            <PermissionGate permission={PERMISSIONS.MC_INQUIRIES_SEND}>
              <Button
                variant="outline"
                size="icon"
                className="h-[30px] w-[30px]"
                aria-label={translate("SETTINGS/MASTER_DATA/OPEN")}
                onClick={() => navigate(SETTINGS_PATHS.masterData)}
              >
                <Settings size={15} />
              </Button>
            </PermissionGate>
            {showReset && (
              <PermissionGate permission={PERMISSIONS.MC_MASTER_DATA_EDIT}>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-red1/30 text-red1 hover:bg-red1/10 hover:text-red1"
                  onClick={() => setShowConfirm(true)}
                >
                  <Trash2 className="h-4 w-4" />
                  {translate("MC_MD_RESET_BTN")}
                </Button>
              </PermissionGate>
            )}
            <PermissionGate permission={PERMISSIONS.MC_IMPORTS_UPLOAD}>
              <Button
                size="sm"
                className="bg-yellow1 text-black hover:bg-yellow2"
                onClick={() =>
                  navigate("/material-compliance/master-data/import")
                }
              >
                <UploadCloud />
                {translate("MC_MD_IMPORT_BTN")}
              </Button>
            </PermissionGate>
          </div>
        }
      />
      <div className="min-h-0 flex-1">{children}</div>

      {showConfirm && (
        <ConfirmDialog
          title={translate("MC_MD_RESET_CONFIRM_TITLE")}
          message={translate("MC_MD_RESET_CONFIRM_MESSAGE")}
          confirmLabel={translate("MC_MD_RESET_CONFIRM_OK")}
          cancelLabel={translate("MC_MD_EDIT_CANCEL")}
          onConfirm={() => resetMutation.mutate()}
          onCancel={() => setShowConfirm(false)}
          icon={IconTrash}
          variant="danger"
          isLoading={resetMutation.isPending}
        />
      )}
    </div>
  )
}
